// packages/web/src/routes/_authed/work/inbox.tsx
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PullToRefresh } from "@/components/vroom/pull-to-refresh";
import { IndexPageHeader, IndexPageHeaderTitle } from "@/components/vroom/index-page-header";
import { tripInboxOptions } from "@/api/trip-declarations/query-options";
import { lodgingInboxOptions } from "@/api/lodging-declarations/query-options";
import type { TripDeclaration, LodgingDeclaration } from "@/api/generated/types.gen";

export const Route = createFileRoute("/_authed/work/inbox")({ component: InboxPage });

function InboxPage() {
  const { t } = useTranslation();
  const { user } = Route.useRouteContext() as { user: { id: string } };
  const navigate = useNavigate();
  const trips = useQuery(tripInboxOptions(user.id));
  const lodgings = useQuery(lodgingInboxOptions(user.id));

  const items = [
    ...(trips.data ?? []).map((tr: TripDeclaration) => ({
      kind: "trip" as const,
      id: tr.id,
      date: new Date(tr.tripDate).toISOString().slice(0, 10),
      // Segments only carry addressId; no label available without a separate lookup.
      summary: summarizeTrip(tr),
      createdByUserId: tr.createdByUserId,
    })),
    ...(lodgings.data ?? []).map((l: LodgingDeclaration) => ({
      kind: "lodging" as const,
      id: l.id,
      date: new Date(l.nightEndedOn).toISOString().slice(0, 10),
      summary: l.lodgingAddressId,
      createdByUserId: l.createdByUserId,
    })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <PullToRefresh
      onRefresh={async () => {
        await Promise.all([trips.refetch(), lodgings.refetch()]);
      }}
    >
      <IndexPageHeader>
        <IndexPageHeaderTitle title={t("work.inbox.title")} />
      </IndexPageHeader>
      <div className="px-4 py-4 md:px-6 md:py-6">
        {items.length === 0 ? (
          <p className="text-ink-500">{t("work.inbox.empty")}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.kind + it.id}>
                <button
                  type="button"
                  className="w-full text-left rounded-4 border-line border bg-surface p-3"
                  onClick={() =>
                    void navigate(
                      // Routes /work/trip/$id and /work/lodging/$id are added in Tasks 50-51.
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      it.kind === "trip"
                        ? ({ to: "/work/trip/$id", params: { id: it.id } } as any)
                        : ({ to: "/work/lodging/$id", params: { id: it.id } } as any),
                    )
                  }
                >
                  <div className="font-medium">{it.date}</div>
                  <div className="text-xs text-ink-500">{it.summary}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PullToRefresh>
  );
}

function summarizeTrip(tr: TripDeclaration): string {
  const segs = tr.segments;
  if (!segs.length) return "";
  // Segments only carry addressId — no label available without a separate fetch.
  const firstId = segs[0]?.addressId ?? "?";
  const lastId = segs[segs.length - 1]?.addressId ?? "?";
  if (firstId === lastId) return firstId;
  return `${firstId} → ${lastId}`;
}
